import { Box, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { SettingRow } from 'components/common/settings-dialog/row';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { AdminPortalDialog } from 'enums/admin-portal';
import { t } from 'i18next';
import { SfxName, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { IProjectorControlMsg } from 'lib_ts/interfaces/machine-msg/i-projector-adjustment';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { OnDemandService } from 'services/on-demand.service';
import { TailscaleService } from 'services/tailscale.service';
import { WebSocketService } from 'services/web-socket.service';
import { useAuthStore } from 'stores/use-auth-store';

// we'll wait for 20 seconds before showing an error toast
const PROJECTOR_TIMEOUT_MS = 30000;

// dummy loading timers for Vention and AMP Calibrations since we aren't getting progress responses from FW for this (just to prevent accidently sending multiple commands)
const VENTION_CALIBRATION_TIMEOUT_MS = 3000;
const AMP_CALIBRATION_TIMEOUT_MS = 3000;

interface IMachineActionDef {
  id: string; // just used as a key
  header: string;
  description: ReactNode;
  buttonLabel: string;
  buttonColor?: RadixColor;
  disabled?: boolean;
  loading?: boolean;
  headerDecoration?: React.ReactNode;
  invisible?: boolean;
  onClick?: () => void;
}

// some actions can only be performed when connected to the machine in question
// those may involve interacting with functionality from the MachineContext instead of our MachinesPortalStore
export const MachineActions = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const {
    machine: connectedMachine,
    isActive,
    playSound,
    openDialog: machineCxOpenDialog,
  } = useContext(MachineContext);

  const [configuringProjector, setConfiguringProjector] = useState(false);
  const [calibratingVention, setCalibratingVention] = useState(false);
  const [calibratingAmp, setCalibratingAmp] = useState(false);

  const projectorConfigTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ventionCalibrationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ampCalibrationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const machine = useMachinesPortalStore((s) => s.machine);
  const openDialog = useMachinesPortalStore((s) => s.openDialog);

  const isOnline = !!machine?.connected;
  // is the user connected to this specific machine
  const connected =
    machine?.machineID === connectedMachine.machineID && isActive;

  const resetProjector = useCallback(() => {
    if (!connected) {
      return;
    }

    // currently all we can do is turn the projector and audio on
    const msg: IProjectorControlMsg = {
      projector_on: true,
      audio_on: true,
    };

    setConfiguringProjector(true);

    projectorConfigTimeoutRef.current = setTimeout(() => {
      setConfiguringProjector(false);
      NotifyHelper.error({
        message_md: `Unable to configure projector: no response received from machine.`,
      });
    }, PROJECTOR_TIMEOUT_MS);

    WebSocketService.send(
      WsMsgType.U2M_ProjectorControl,
      msg,
      'Reset Projector button'
    );
  }, [connected]);

  const turnOffProjector = useCallback(() => {
    if (!connected) {
      return;
    }

    // currently all we can do is turn the projector and audio on
    const msg: IProjectorControlMsg = {
      projector_on: false,
      audio_on: false,
    };

    setConfiguringProjector(true);

    projectorConfigTimeoutRef.current = setTimeout(() => {
      setConfiguringProjector(false);
      NotifyHelper.error({
        message_md: `Unable to configure projector: no response received from machine.`,
      });
    }, PROJECTOR_TIMEOUT_MS);

    WebSocketService.send(
      WsMsgType.U2M_ProjectorControl,
      msg,
      'Turn Off Projector button'
    );
  }, [connected]);

  useEffect(() => {
    const callback = () => {
      if (projectorConfigTimeoutRef.current) {
        clearTimeout(projectorConfigTimeoutRef.current);
        projectorConfigTimeoutRef.current = null;
      }

      setConfiguringProjector(false);
    };

    WebSocketHelper.on(WsMsgType.M2U_ProjectorControlResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_ProjectorControlResponse, callback);
    };
  }, []);

  const runVentionCalibration = useCallback(async () => {
    if (!machine?.machineID) {
      return;
    }

    // dummy loading state until we get real progress responses from FW - prevents accidentally sending multiple calibration commands
    setCalibratingVention(true);

    await AdminMachinesService.getInstance().calibrateVentionPosition({
      machineID: machine.machineID,
    });

    ventionCalibrationTimeoutRef.current = setTimeout(() => {
      setCalibratingVention(false);
    }, VENTION_CALIBRATION_TIMEOUT_MS);
  }, [machine?.machineID]);

  const runAmpCalibration = useCallback(async () => {
    if (!machine?.machineID) {
      return;
    }

    // dummy loading state until we get real progress responses from FW - prevents accidentally sending multiple calibration commands
    setCalibratingAmp(true);

    await AdminMachinesService.getInstance().calibrateAmpPosition({
      machineID: machine.machineID,
    });

    ampCalibrationTimeoutRef.current = setTimeout(() => {
      setCalibratingAmp(false);
    }, AMP_CALIBRATION_TIMEOUT_MS);
  }, [machine?.machineID]);

  const visibleActions = useMemo(() => {
    if (!machine) {
      return [];
    }

    const ALL_MACHINE_ACTIONS: IMachineActionDef[] = [
      {
        id: 'test-projector-audio',
        header: 'admin.test-projector-audio',
        description: t('admin.test-projector-audio-msg'),
        disabled: !connected,
        buttonLabel: 'admin.test-audio',
        onClick: () => playSound(SfxName.START),
      },
      {
        id: 'export-sessions',
        header: 'admin.export-sessions',
        description: t('admin.export-sessions-msg'),
        buttonLabel: 'admin.export-sessions',
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.ExportSessions,
            machines: [machine],
          }),
      },
      {
        id: 'health-check',
        invisible: !userRoleFeatureFlags.healthCheck,
        headerDecoration: <SuperAdminIcon />,
        header: 'Health Check',
        description:
          'The machine will run a full diagnostic, display a Pass/Fail result, and list any failed submodules with details.',
        buttonLabel: 'Run Health Check',
        disabled: !connected,
        onClick: () => machineCxOpenDialog(MachineDialogMode.HealthCheck),
      },
      {
        id: 'restore-pitch-lists',
        invisible: !userRoleFeatureFlags.adminMachines.restoreLists,
        headerDecoration: <SuperAdminIcon />,
        header: 'Restore Pitch Lists',
        description:
          'Restore previously deleted pitch lists associated to this machine.',
        buttonLabel: 'Restore Lists',
        buttonColor: RADIX.COLOR.WARNING,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.RestoreList,
            machines: [machine],
          }),
      },
      {
        id: 'reassign-pitch-lists',
        header: t('settings.reassign-pitch-lists'),
        description: t('settings.reassign-pitch-lists-msg'),
        buttonLabel: 'settings.reassign-pitch-lists',
        buttonColor: RADIX.COLOR.WARNING,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.Reassign,
            machines: [machine],
          }),
      },
      {
        id: 'reset-connections',
        header: t('admin.reset-connections'),
        description: t('admin.reset-connections-msg'),
        buttonLabel: 'admin.reset-connections',
        buttonColor: RADIX.COLOR.WARNING,
        disabled: !isOnline,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.ResetConnections,
            machines: [machine],
          }),
      },
      {
        id: 'reset-projector-settings',
        header: 'Reset Projector to Default Settings',
        headerDecoration: <SuperAdminIcon />,
        description: (
          <Box>
            <Text>
              Set the projector to the default settings recommended by Trajekt.
            </Text>
            <br />
            <Text>
              Note: This may take up to {PROJECTOR_TIMEOUT_MS / 1000} seconds.
            </Text>
          </Box>
        ),
        invisible: !userRoleFeatureFlags.adminMachines.projectorReset,
        disabled: !connected || configuringProjector,
        loading: configuringProjector,
        buttonLabel: 'Reset Projector',
        buttonColor: RADIX.COLOR.WARNING,
        onClick: () => resetProjector(),
      },
      {
        id: 'turn-off-projector',
        header: 'Turn Off Projector',
        headerDecoration: <SuperAdminIcon />,
        description: 'Turn off projector and audio output.',
        invisible: !userRoleFeatureFlags.adminMachines.projectorShutdown,
        disabled: !connected || configuringProjector,
        loading: configuringProjector,
        buttonLabel: 'Turn Off Projector',
        buttonColor: RADIX.COLOR.WARNING,
        onClick: () => turnOffProjector(),
      },
      {
        id: 'upgrade-firmware',
        invisible: !userRoleFeatureFlags.adminMachines.upgradeFirmware,
        headerDecoration: <SuperAdminIcon />,
        header: 'Upgrade Firmware',
        description:
          "Update the machine's firmware to a selected environment (Staging, Dev, or Prod).",
        buttonLabel: 'Upgrade Firmware',
        buttonColor: RADIX.COLOR.WARNING,
        disabled: !isOnline,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.FWUpgradeDate,
            machines: [machine],
          }),
      },
      {
        id: 'restart-os',
        header: t('common.restart'),
        description: t('admin.restart-msg'),
        buttonLabel: 'common.restart',
        buttonColor: RADIX.COLOR.WARNING,
        disabled: !isOnline,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.RestartOS,
            machines: [machine],
          }),
      },
      {
        // should be mutually exclusive w/ resume install
        id: 'new-install',
        invisible:
          !userRoleFeatureFlags.adminMachines.install ||
          // existing installation is in progress
          (!!machine.last_installation && !machine.last_installation.end_date),
        headerDecoration: <SuperAdminIcon />,
        header: 'New Install',
        description:
          'Set up this machine in a new facility or after relocation.',
        buttonLabel: 'New Install',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline || !connected,
        onClick: () => machineCxOpenDialog(MachineDialogMode.Installation),
      },
      {
        // should be mutually exclusive w/ new install
        id: 'resume-install',
        invisible:
          !userRoleFeatureFlags.adminMachines.install ||
          // nothing to resume
          !machine.last_installation ||
          // already completed
          !!machine.last_installation.end_date,
        headerDecoration: <SuperAdminIcon />,
        header: 'Resume Install',
        description: 'Resume an existing installation.',
        buttonLabel: 'Resume Install',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline || !connected,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.ConfirmInstall,
            machines: [machine],
          }),
      },
      {
        id: 'restart-fw',
        invisible: !userRoleFeatureFlags.adminMachines.restartFirmware,
        headerDecoration: <SuperAdminIcon />,
        header: 'Restart FW',
        description: 'Restart the firmware of this machine.',
        buttonLabel: 'Restart FW',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline || !connected,
        onClick: () =>
          openDialog({
            type: AdminPortalDialog.RestartFW,
            machines: [machine],
          }),
      },
      {
        id: 'relay-restart',
        invisible: !userRoleFeatureFlags.adminMachines.relayRestart,
        headerDecoration: <SuperAdminIcon />,
        header: 'Relay Restart',
        description: 'Restart the relay process.',
        buttonLabel: 'Restart Relay',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline || !connected,
        onClick: () =>
          WebSocketService.send(
            WsMsgType.Process_RelayRestart,
            {},
            'Relay Restart button'
          ),
      },
      {
        id: 'clear-tilt-lut',
        invisible: !userRoleFeatureFlags.adminMachines.tiltLutReset,
        headerDecoration: <SuperAdminIcon />,
        header: 'Clear Tilt Lookup Table',
        description: 'Reset the tilt calibration lookup table.',
        buttonLabel: 'Clear Tilt Lookup Table',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline || !connected,
        onClick: () =>
          WebSocketService.send(
            WsMsgType.Process_TiltLutReset,
            {},
            'Clear Tilt LUT button'
          ),
      },
      {
        id: 'vention-calibration',
        invisible: !userRoleFeatureFlags.adminMachines.ventionCalibration,
        headerDecoration: <SuperAdminIcon />,
        header: 'Calibrate Vention Position from Unknown State',
        description: (
          <Box>
            <Text>
              Run automated balancing process for Vention Position Controllers
              when in an unknown state.
            </Text>
            <br />
            <Text>Should only be run while under technician supervision.</Text>
          </Box>
        ),
        buttonLabel: 'Calibrate Vention Position',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline,
        onClick: runVentionCalibration,
        loading: calibratingVention,
      },
      {
        id: 'amp-calibration',
        invisible: !userRoleFeatureFlags.adminMachines.ampCalibration,
        headerDecoration: <SuperAdminIcon />,
        header: 'Calibrate AMP Position on Install',
        description:
          'Run automated calibration of AMP Position Controllers during machine installation or manufacturing.',
        buttonLabel: 'Calibrate AMP Position',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline,
        onClick: runAmpCalibration,
        loading: calibratingAmp,
      },
      {
        id: 'tailscale-key',
        invisible: !userRoleFeatureFlags.adminMachines.createTailscaleKey,
        headerDecoration: <SuperAdminIcon />,
        header: 'Create Tailscale Key',
        description: 'Generate and send a Tailscale key to the machine.',
        buttonLabel: 'Create Key',
        buttonColor: RADIX.COLOR.DANGER,
        disabled: !isOnline,
        onClick: async () => {
          const success = await TailscaleService.getInstance().createAuthKey(
            machine.machineID
          );

          if (success) {
            NotifyHelper.success({
              message_md: `Created key for ${machine.machineID}!`,
            });
          }
        },
        loading: calibratingAmp,
      },
      {
        id: 'calibration-rebuild',
        invisible:
          !userRoleFeatureFlags.adminMachines.onDemandCalibrationRebuild,
        headerDecoration: <SuperAdminIcon />,
        header: 'On-Demand - Calibration Rebuild',
        description: 'Rebuild the calibration of this machine.',
        buttonLabel: 'Rebuild',
        buttonColor: RADIX.COLOR.DANGER,
        onClick: async () => {
          const started =
            await OnDemandService.getInstance().calibrationRebuild({
              machineID: machine.machineID,
              ball_type: machine.ball_type,
            });

          if (!started) {
            NotifyHelper.error({
              message_md: `Failed to rebuild calibration for ${machine.machineID}.`,
            });
            return;
          }

          NotifyHelper.success({
            message_md: `Calibration rebuild for ${machine.machineID} has been started.`,
          });
        },
      },

      {
        id: 'calibration-reset',
        invisible: !userRoleFeatureFlags.adminMachines.onDemandCalibrationReset,
        headerDecoration: <SuperAdminIcon />,
        header: 'On-Demand - Calibration Reset',
        description: 'Reset the calibration of this machine.',
        buttonLabel: 'Reset',
        buttonColor: RADIX.COLOR.DANGER,
        onClick: async () => {
          const started = await OnDemandService.getInstance().calibrationReset({
            machineID: machine.machineID,
            ball_type: machine.ball_type,
          });

          if (!started) {
            NotifyHelper.error({
              message_md: `Failed to reset calibration for ${machine.machineID}.`,
            });
            return;
          }

          NotifyHelper.success({
            message_md: `Calibration reset for ${machine.machineID} has been started.`,
          });
        },
      },
    ];

    return ALL_MACHINE_ACTIONS.filter(({ invisible }) => !invisible);
  }, [
    userRoleFeatureFlags,
    connected,
    isOnline,
    configuringProjector,
    calibratingAmp,
    calibratingVention,
    machine,
    playSound,
    openDialog,
    resetProjector,
    turnOffProjector,
  ]);

  return (
    <SettingForm>
      {visibleActions.map((action, i) => (
        <SettingRow
          key={action.id}
          header={action.header}
          headerDecoration={action.headerDecoration}
          description={action.description}
          input={
            <SettingButton
              label={action.buttonLabel}
              color={action.buttonColor}
              onClick={action.onClick}
              disabled={action.disabled}
              loading={action.loading}
            />
          }
          separatorAfter={i < visibleActions.length - 1}
        />
      ))}
    </SettingForm>
  );
};
